/**
 * Public property map — every property matching the current filters, plotted
 * as a price-labelled pin, initially centred on the visitor's own location
 * when the browser grants it (falls back to a Nairobi-wide view otherwise).
 *
 * `GET properties/public/` is paginated (20/page) with no "give me everything"
 * mode, so this page walks every page for the current filter set to build the
 * full pin set — small, bounded per-listing-set fetch, not a live map tile
 * data source. Filters mirror PublicPropertyBrowsePage.tsx's filter-bar
 * exactly (same fields, same query-param contract) so a "Map view" link from
 * that page's filter state lands here pre-filtered, and vice versa.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { parseApiError } from '../../lib/apiError';
import { listPublicProperties } from '../../api/endpoints/properties';
import { PROPERTY_TYPES, type PropertyListRow, type PropertyType } from '../../types/property';
import { deriveFromPrice, formatKesCompact } from '../../lib/propertyListing';
import { ROUTES } from '../../routes/paths';

function readTypeParam(searchParams: URLSearchParams): PropertyType | '' {
  const value = searchParams.get('property_type');
  return value !== null && (PROPERTY_TYPES as readonly string[]).includes(value)
    ? (value as PropertyType)
    : '';
}

/** Nairobi — the same default this app's other maps centre on when there's nothing more specific to go on. */
const DEFAULT_CENTER: [number, number] = [-1.286389, 36.817223];
const DEFAULT_ZOOM = 6;
const LOCATED_ZOOM = 12;

function priceIcon(label: string): L.DivIcon {
  return L.divIcon({
    className: 'map-pin',
    html: `<span class="map-pin__label">${label}</span>`,
    iconSize: [1, 1], // the label sizes itself via CSS; Leaflet just needs an anchor box
    iconAnchor: [0, 0],
  });
}

/** Recenters the map once, when the visitor's own location resolves after the map has already mounted. */
function RecenterOn({ position, zoom }: { position: [number, number] | null; zoom: number }): null {
  const map = useMap();
  useEffect(() => {
    if (position !== null) map.setView(position, zoom);
  }, [position, zoom, map]);
  return null;
}

export function PublicPropertyMapPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('search') ?? '');
  const [propertyType, setPropertyType] = useState<PropertyType | ''>(() => readTypeParam(searchParams));
  const [minPrice, setMinPrice] = useState(() => searchParams.get('min_price') ?? '');
  const [maxPrice, setMaxPrice] = useState(() => searchParams.get('max_price') ?? '');
  const [bedrooms, setBedrooms] = useState(() => searchParams.get('bedrooms') ?? '');

  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (typeof navigator === 'undefined' || navigator.geolocation === undefined) return;
    navigator.geolocation.getCurrentPosition(
      (position) => setUserLocation([position.coords.latitude, position.coords.longitude]),
      // Denied, unavailable, or timed out — the map just stays on its default view.
      () => undefined,
      { enableHighAccuracy: false, timeout: 8000 },
    );
  }, []);

  const filters = { search, propertyType, minPrice, maxPrice, bedrooms };

  const query = useQuery({
    queryKey: ['public-properties-map', filters],
    queryFn: async () => {
      const params = {
        search: search === '' ? undefined : search,
        property_type: propertyType === '' ? undefined : propertyType,
        min_price: minPrice === '' ? undefined : minPrice,
        max_price: maxPrice === '' ? undefined : maxPrice,
        bedrooms: bedrooms === '' ? undefined : bedrooms,
      };

      const results: PropertyListRow[] = [];
      let page = 1;
      for (;;) {
        const response = await listPublicProperties({ ...params, page });
        results.push(...response.results);
        if (response.next === null || response.results.length === 0) break;
        page += 1;
      }
      return results;
    },
  });

  const properties = query.data ?? [];
  const pins = properties
    .map((property) => {
      const latitude = property.latitude !== null ? Number(property.latitude) : null;
      const longitude = property.longitude !== null ? Number(property.longitude) : null;
      if (latitude === null || longitude === null || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return null;
      }
      const fromPrice = deriveFromPrice(property.units);
      return { property, position: [latitude, longitude] as [number, number], fromPrice };
    })
    .filter((pin): pin is { property: PropertyListRow; position: [number, number]; fromPrice: number | null } => pin !== null);

  return (
    <section>
      <h1>Properties near you</h1>

      <p>
        <Link to={ROUTES.publicProperties}>List view</Link>
      </p>

      <form
        className="filter-bar"
        onSubmit={(event) => event.preventDefault()}
      >
        <div className="filter-bar__field">
          <label htmlFor="map-search">Search</label>
          <input id="map-search" value={search} onChange={(event) => setSearch(event.target.value)} />
        </div>

        <div className="filter-bar__field">
          <label htmlFor="map-type">Type</label>
          <select
            id="map-type"
            value={propertyType}
            onChange={(event) => setPropertyType(event.target.value as PropertyType | '')}
          >
            <option value="">Any</option>
            {PROPERTY_TYPES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-bar__field">
          <label htmlFor="map-min-price">Min rent</label>
          <input
            id="map-min-price"
            type="number"
            value={minPrice}
            onChange={(event) => setMinPrice(event.target.value)}
          />
        </div>

        <div className="filter-bar__field">
          <label htmlFor="map-max-price">Max rent</label>
          <input
            id="map-max-price"
            type="number"
            value={maxPrice}
            onChange={(event) => setMaxPrice(event.target.value)}
          />
        </div>

        <div className="filter-bar__field">
          <label htmlFor="map-bedrooms">Bedrooms</label>
          <input
            id="map-bedrooms"
            type="number"
            value={bedrooms}
            onChange={(event) => setBedrooms(event.target.value)}
          />
        </div>
      </form>

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}
      {query.isSuccess && pins.length === 0 && <p>No properties with a map location match your search.</p>}

      {query.isSuccess && pins.length > 0 && (
        <MapContainer
          center={userLocation ?? DEFAULT_CENTER}
          zoom={userLocation !== null ? LOCATED_ZOOM : DEFAULT_ZOOM}
          scrollWheelZoom
          className="public-map"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <RecenterOn position={userLocation} zoom={LOCATED_ZOOM} />
          {pins.map(({ property, position, fromPrice }) => (
            <Marker
              key={property.id}
              position={position}
              icon={priceIcon(fromPrice !== null ? formatKesCompact(fromPrice) : 'Full')}
            >
              <Popup>
                <strong>{property.name}</strong>
                <br />
                {property.city}, {property.state}
                <br />
                {fromPrice !== null ? `From KES ${fromPrice.toLocaleString('en-KE')}` : 'Fully occupied'}
                <br />
                <Link to={`${ROUTES.publicProperties}/${property.id}`}>View details</Link>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      )}
    </section>
  );
}

export default PublicPropertyMapPage;
